import React, { useEffect, useRef, useState } from 'react';
import {
  FlatList, Pressable, StyleSheet, Text, View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import SearchBar from '../../components/SearchBar';
import Button from '../../components/Button';
import FilterIcon16 from '../../components/16pxIcons/FilterIcon';
import AlertModal from '../../components/AlertModal';
import getTickets from '../../requests/Tickets/GetTickets';
import getUsers from '../../requests/Users/GetUsers';
import getAssets from '../../requests/Assets/GetAssets';
import getUserGroups from '../../requests/Users/GetUserGroups';
import {
  AssetInstance, Ticket, UserGroup, UserInstance, UserPermission,
} from '../../constants/Types';
import { DateRange, DateSelectionRange } from '../../redux/dateRangeSlice';
import { Building } from '../LocationManagement/components/AddBuildingModal';
import { SelectedTicketState, updateSelectedTicket } from '../../redux/selectedTicketSlice';
import TicketList from './components/TicketList';
import SelectedTicket from '../Tickets/components/SelectedTicket';
import TicketsFilter from '../Tickets/components/TicketsFilter';
import QuickFilters, {
  OpenTicketsShown,
  OpenTicketStatistics,
  QuickFiltersRef,
} from './components/QuickFilters';
import DownArrow16 from '../../components/16pxIcons/DownArrow16';

type TicketSortMode = 'time' | 'priority';

const TicketsV2 = () => {
  const dispatch = useDispatch();
  const selectedTicketDetails: SelectedTicketState = useSelector((state:
    { selectedTicket: SelectedTicketState }) => state.selectedTicket);
  const selectedTicket: Ticket = selectedTicketDetails?.ticket;
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const selectedBuilding: Building | null = (buildings && selectedBuildingId)
    ? buildings[selectedBuildingId] : null;
  const {
    isAdmin, permissions, firstName, lastName,
  } = useSelector((state:
    {
      user: {
        firstName: string, lastName: string, isAdmin: boolean, permissions: UserPermission[],
      }
    }) => state.user);

  const canViewTickets: boolean = isAdmin
    || permissions.includes(UserPermission.TICKETS_READ);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [pinnedTickets, setPinnedTickets] = useState<Ticket[]>([]);
  const [ticketCount, setTicketCount] = useState<number | undefined>(undefined);
  const [counts, setCounts] = useState<OpenTicketStatistics>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [, setRefreshing] = useState<boolean>(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [users, setUsers] = useState<UserInstance[]>([]);
  const [assets, setAssets] = useState<AssetInstance[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<DateSelectionRange>(1);
  const [range, setRange] = useState<DateRange>({ start: null, end: null });
  const [openTicketsSelection, setOpenTicketsSelection] = useState<OpenTicketsShown | null>(null);
  const [offset, setOffset] = useState<number>(0);
  const [otherPriorityFilter, setOtherPriorityFilter] = useState<boolean>(false);
  const [lowPriorityFilter, setLowPriorityFilter] = useState<boolean>(false);
  const [mediumPriorityFilter, setMediumPriorityFilter] = useState<boolean>(false);
  const [highPriorityFilter, setHighPriorityFilter] = useState<boolean>(false);
  const [criticalPriorityFilter, setCriticalPriorityFilter] = useState<boolean>(false);
  const [doneFilter, setDoneFilter] = useState<boolean>(false);
  const [inProgressFilter, setInProgressFilter] = useState<boolean>(false);
  const [notStartedFilter, setNotStartedFilter] = useState<boolean>(false);
  const [slaOverdueFilter, setSlaOverdueFilter] = useState<boolean>(false);
  const [slaSort, setSlaSort] = useState<string>('');
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [selectedAssigneeName, setSelectedAssigneeName] = useState<string>('');
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [sortMode, setSortMode] = useState<TicketSortMode>('time');

  const flatList = useRef<FlatList>(null);
  const ticketActivityRef = useRef(null);
  const ticketCountWidgetRef = useRef<QuickFiltersRef>(null);
  const originalFetchTime = useRef(new Date()).current;
  const limit: number = 15;

  const setSelectedTicket = (ticket: Ticket | null) => dispatch(updateSelectedTicket(ticket));

  const showAlert = (title: string, message: string | null = null, error: boolean = true) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertVisible(true);
  };

  const updatePinnedTickets = (updatedTickets: Ticket[]) => {
    if (updatedTickets?.length > 3) updatedTickets.shift();
    setPinnedTickets(updatedTickets ?? []);
  };

  const loadTickets = (pageNumber: number) => {
    setOffset(pageNumber * limit);
    setLoading(true);
  };

  const updateSelectedTicketInList = (updatedTicket: Ticket) => {
    setSelectedTicket(updatedTicket);
    setTickets((previous) => previous
      .map((t: Ticket) => (t.id === updatedTicket.id ? updatedTicket : t)));
    updatePinnedTickets((pinnedTickets ?? [])
      .map((t: Ticket) => (t.id === updatedTicket.id ? updatedTicket : t)));
    ticketCountWidgetRef?.current?.refresh?.();
    ticketActivityRef?.current?.refreshActivity?.();
  };

  const resetAllFilters = () => {
    setOtherPriorityFilter(false);
    setLowPriorityFilter(false);
    setMediumPriorityFilter(false);
    setHighPriorityFilter(false);
    setCriticalPriorityFilter(false);
    setDoneFilter(false);
    setInProgressFilter(false);
    setNotStartedFilter(false);
    setSlaOverdueFilter(false);
    setSelectedAssetId('');
    setSelectedUserGroupId('');
    setSelectedAssignee('');
    setSelectedAssigneeName('');
    setSelectedLocation('');
    setSlaSort('');
    setRange({ start: null, end: null });
    setLoading(true);
  };

  useEffect(() => {
    if (!selectedBuildingId) return;

    getUsers().then((response) => {
      if (response?.items?.length > 0) setUsers(response.items);
    });
    getAssets({ locationId: selectedBuildingId, enableSubLocationsLookUp: true }).then((result) => {
      if (result?.assets?.length > 0) setAssets(result.assets);
    });
    getUserGroups().then((response) => {
      if (response?.UserGroups?.length > 0) setUserGroups(response.UserGroups);
    });
  }, [selectedBuildingId]);

  useEffect(() => {
    if (!selectedBuildingId) {
      setLoading(false);
      return;
    }

    if (!loading) return;

    setShowSpinner(true);
    const quickInProgressOnly = openTicketsSelection === OpenTicketsShown.OPEN_IN_PROGRESS;
    const quickOpenSelection = !!openTicketsSelection && !quickInProgressOnly;
    getTickets({
      other: otherPriorityFilter,
      low: lowPriorityFilter,
      moderate: mediumPriorityFilter,
      high: highPriorityFilter,
      critical: criticalPriorityFilter,
      done: !openTicketsSelection && doneFilter,
      inprogress: !!openTicketsSelection || inProgressFilter,
      reported: quickOpenSelection || notStartedFilter,
      assignee: selectedAssignee,
      buildingId: selectedBuildingId,
      assetId: selectedAssetId,
      usergroupIds: [selectedUserGroupId],
      startTime: range.start,
      endTime: range.end,
      locationId: selectedLocation,
      overdue: openTicketsSelection === OpenTicketsShown.OPEN_OVERDUE
        || openTicketsSelection === OpenTicketsShown.OPEN_ASSIGNED_TO_ME_OVERDUE
        || slaOverdueFilter ? true : null,
      limit,
      offset,
      textSearch: searchValue,
      sortField: sortMode === 'priority' ? 'priority' : undefined,
    }).then((response) => {
      if (response) {
        const newTickets: Ticket[] = response.tickets ?? [];
        setTickets(newTickets);
        setTicketCount(response.count ?? response.statistics?.totalTickets);
      } else showAlert('Error', 'Failed to load tickets');
    }).catch(() => showAlert('Error', 'Failed to load tickets')).finally(() => {
      setLoading(false);
      setRefreshing(false);
      setShowSpinner(false);
    });
  }, [loading, selectedBuildingId]);

  useEffect(() => {
    setLoading(true);
  }, [
    otherPriorityFilter,
    lowPriorityFilter,
    mediumPriorityFilter,
    highPriorityFilter,
    criticalPriorityFilter,
    doneFilter,
    inProgressFilter,
    notStartedFilter,
    slaOverdueFilter,
    selectedAssetId,
    selectedAssignee,
    selectedUserGroupId,
    selectedLocation,
    slaSort,
    range,
    openTicketsSelection,
    searchValue,
    offset,
    sortMode,
  ]);

  let count: number = ticketCount;
  if (openTicketsSelection && counts) {
    count = openTicketsSelection === OpenTicketsShown.OPEN_IN_PROGRESS
      ? (counts?.openInProgress ?? counts?.open)
      : counts[openTicketsSelection];
  }
  const hasSelectedTicket: boolean = !!selectedTicket;

  if (!canViewTickets) {
    return <View style={styles.screen}>
      <View style={styles.filterBanner}>
        <Text style={styles.sectionTitle}>Tickets</Text>
      </View>
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No permission to view tickets</Text>
      </View>
    </View>;
  }

  return <View style={styles.screen}>
    <View style={styles.filterBanner}>
      <View style={styles.searchBanner}>
        <Text style={styles.ticketsTitle}>Tickets</Text>
        <View style={styles.bannerControls}>
          <SearchBar
            apply={setSearchValue}
            loading={loading}
            placeholder='Search tickets by name or ID'
          />
        </View>
      </View>
      <View style={styles.bannerWidget}>
        <View style={styles.bannerWidgetRow}>
          <View style={styles.quickFiltersContainer}>
            <QuickFilters
              openTicketsSelection={openTicketsSelection}
              setOpenTicketsSelection={setOpenTicketsSelection}
              counts={counts}
              setCounts={setCounts}
              ref={ticketCountWidgetRef}
            />
          </View>
          <View style={styles.sortAndFilterContainer}>
            <View style={styles.sortToggleContainer}>
              <Text style={styles.sortLabel}>Sort by:</Text>
              <Pressable
                onPress={() => setSortMode((currentMode) => (
                  currentMode === 'time' ? 'priority' : 'time'
                ))}
                style={styles.sortToggleButton}
              >
                <Text style={styles.sortToggleText}>
                  {sortMode === 'time' ? 'Time' : 'Priority'}
                </Text>
                <DownArrow16 color={Colors.light.hereworksBlack600} />
              </Pressable>
            </View>
            <Button
              title='Filters'
              children={<FilterIcon16 style={{ marginStart: 8 }} />}
              onPress={() => setFilterOpen(!filterOpen)}
              style={styles.filterButton}
            />
          </View>
        </View>
      </View>
    </View>

    <View style={styles.contentRow}>
      <View style={[styles.sidebar, !hasSelectedTicket && styles.sidebarFullWidth]}>
        <TicketList
          newReports={false}
          showSpinner={showSpinner}
          loading={loading}
          setLoading={setLoading}
          setRefreshing={setRefreshing}
          tickets={tickets}
          pinnedTickets={pinnedTickets}
          updatePinnedTickets={updatePinnedTickets as any}
          flatList={flatList}
          renderFilterTags={() => <View />}
          selectedTicket={selectedTicket}
          setModalVisible={() => { }}
          setSelectedTicket={setSelectedTicket as any}
          originalFetchTime={originalFetchTime}
          listSize={limit}
          count={count}
          loadTickets={loadTickets}
        />
      </View>

      {hasSelectedTicket && <View style={styles.mainSection}>
        <SelectedTicket
          showAlert={showAlert}
          users={users}
          selectedTicket={selectedTicket}
          renderSharePane={() => <View />}
          renderMoreOptions={() => <View />}
          moreOptionsVisible={false}
          selectedBuilding={selectedBuilding?.LocationId}
          imageModalVisible={false}
          setImageModalVisible={() => { }}
          ticketActivityRef={ticketActivityRef}
          showSpinner={showSpinner}
          name={`${firstName ?? ''} ${lastName ?? ''}`}
          closureModalVisible={false}
          setClosureModalVisible={() => { }}
          resolve={() => { }}
          setLoading={setLoading}
          setShowSpinner={setShowSpinner}
          userGroups={userGroups}
          updateSelectedTicketInList={updateSelectedTicketInList}
          asset={assets?.find((a: AssetInstance) => a.id === selectedTicket?.assetId) ?? null}
          hide={() => { }}
        />
      </View>}
    </View>

    {filterOpen && <View style={styles.filterOverlay} pointerEvents='box-none'>
      <TicketsFilter
        onConfirm={(start, end) => setRange({ start, end })}
        range={range}
        selectedRange={selectedRange}
        setSelectedRange={setSelectedRange}
        users={users}
        assets={assets}
        templates={[]}
        setLoading={setLoading}
        setFilterOpen={setFilterOpen}
        setTickets={setTickets}
        setSelectedTicket={setSelectedTicket as any}
        userGroups={userGroups}
        otherPriorityFilter={otherPriorityFilter}
        setOtherPriorityFilter={setOtherPriorityFilter}
        lowPriorityFilter={lowPriorityFilter}
        setLowPriorityFilter={setLowPriorityFilter}
        mediumPriorityFilter={mediumPriorityFilter}
        setMediumPriorityFilter={setMediumPriorityFilter}
        highPriorityFilter={highPriorityFilter}
        setHighPriorityFilter={setHighPriorityFilter}
        criticalPriorityFilter={criticalPriorityFilter}
        setCriticalPriorityFilter={setCriticalPriorityFilter}
        doneFilter={doneFilter}
        setDoneFilter={setDoneFilter}
        inProgressFilter={inProgressFilter}
        setInProgressFilter={setInProgressFilter}
        notStartedFilter={notStartedFilter}
        setNotStartedFilter={setNotStartedFilter}
        slaOverdueFilter={slaOverdueFilter}
        setSlaOverdueFilter={setSlaOverdueFilter}
        slaSort={slaSort}
        setSlaSort={setSlaSort}
        selectedAssignee={selectedAssignee}
        setSelectedAssignee={setSelectedAssignee}
        selectedAssigneeName={selectedAssigneeName}
        setSelectedAssigneeName={setSelectedAssigneeName}
        selectedAssetId={selectedAssetId}
        setSelectedAssetId={setSelectedAssetId}
        selectedUserGroupId={selectedUserGroupId}
        setSelectedUserGroupId={setSelectedUserGroupId}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        setSelectedFloor={(() => { }) as any}
        filterShown={filterOpen}
        saveFilterToggled={false}
        setSaveFilterToggled={(() => { }) as any}
        slaNames={[]}
        resetAllFilters={resetAllFilters}
      />
    </View>}

    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      setModalVisible={setAlertVisible}
    />
</View>;
};

export default TicketsV2;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: Colors.light.hereworksBackground,
  },
  filterBanner: {
    position: 'relative',
    zIndex: 20,
    elevation: 20,
    width: '100%',
    minHeight: 100,
    backgroundColor: Colors.light.hereworksWhite500,
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  bannerControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bannerWidget: {
    marginTop: 12,
    padding: 16,
    backgroundColor: Colors.light.plainBackground,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
  },
  bannerWidgetRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  quickFiltersContainer: {
    flex: 1,
    marginRight: 12,
  },
  sortAndFilterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sortToggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
  },
  sortLabel: {
    color: Colors.light.hereworksBlack400,
    fontSize: 14,
    fontFamily: 'Roboto_400Regular',
    marginRight: 2,
  },
  sortToggleButton: {
    paddingVertical: 6,
    paddingHorizontal: 3,
    borderRadius: 8,
    borderColor: Colors.light.hereworksBlue300,
    minWidth: 78,
    alignItems: 'center',
    flexDirection: 'row',
  },
  sortToggleText: {
    color: Colors.light.hereworksBlue500,
    fontSize: 14,
    fontFamily: 'Roboto_400Regular',
  },
  filterOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 40,
    elevation: 40,
  },
  contentRow: {
    zIndex: 1,
    flex: 1,
    flexDirection: 'row',
  },
  sidebar: {
    minWidth: 320,
    maxWidth: '25%',
    width: '25%',
    backgroundColor: Colors.light.hereworksWhite500,
    paddingVertical: 12,
    paddingHorizontal: 0,
    paddingLeft: 24,
  },
  sidebarFullWidth: {
    minWidth: undefined,
    maxWidth: '100%',
    width: '100%',
    flex: 1,
    borderRightWidth: 0,
    paddingRight: 24,
  },
  mainSection: {
    flex: 1,
    backgroundColor: Colors.light.hereworksWhite500,
    padding: 12,
  },
  sectionTitle: {
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_500Medium',
  },
  ticketsTitle: {
    fontSize: 32,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
    marginBottom: 8,
  },
  filterButton: {
    alignSelf: 'flex-start',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    color: Colors.light.hereworksBlack400,
    fontSize: 16,
    fontFamily: 'Roboto_400Regular',
  },
  searchBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
});
